/**
 * Case Data Adapter
 *
 * Provides unified data loading for Case components using hybrid approach:
 * 1. List files (12 essential fields for list views)
 * 2. Detail files (Raw v2.0.0 schema with full specifications)
 * 3. Pricing files (regularPrice, currentPrice)
 *
 * This adapter provides:
 * - List loading with pricing (getAllItems, getCurrentItems, getDiscontinuedItems)
 * - Raw detail loading (getItemDetail, getRawDetail)
 *
 * Usage:
 * ```typescript
 * const adapter = getCaseDataAdapter()
 * const allCases = await adapter.getAllItems()           // List view
 * const caseDetail = await adapter.getItemDetail('id')   // Detail view
 * ```
 */

import type { PriceInfo, PricingData } from '../common/GenericDataAdapter'
import type { CaseListItem } from '../../models/case/case-list-model'
import { isCaseRawModel, type CaseRawModel } from '../../models/case/case-raw-model'
import type { CaseDetailModel } from '../../models/case/case-detail-model'

/**
 * Case Data Adapter Class
 *
 * Combines List/Pricing loading with Raw detail conversion
 */
class CaseDataAdapter {
  private readonly basePath = '/data/case'

  private listCache: Map<string, CaseListItem[]> = new Map()
  private pricingCache: Map<string, PriceInfo> = new Map()
  private pricingLoadTime = 0
  private detailCache: Map<string, CaseRawModel> = new Map()
  private readonly PRICING_TTL = 300000 // 5 minutes
  private readonly DETAIL_TTL = 600000 // 10 minutes

  /**
   * Load list file (current or discontinued)
   */
  private async loadListFile(status: 'current' | 'discontinued'): Promise<CaseListItem[]> {
    const cacheKey = `case-${status}-list`

    if (this.listCache.has(cacheKey)) {
      return this.listCache.get(cacheKey)!
    }

    const response = await fetch(`${this.basePath}/case-${status}-list.json`)
    if (!response.ok) {
      throw new Error(`Failed to load Case ${status} list: ${response.statusText}`)
    }

    const data: CaseListItem[] = await response.json()
    this.listCache.set(cacheKey, data)
    return data
  }

  /**
   * Load pricing file (current or discontinued)
   */
  private async loadPricingFile(status: 'current' | 'discontinued'): Promise<Map<string, PriceInfo>> {
    const now = Date.now()

    // Check if pricing data is stale
    if (this.pricingCache.size > 0 && (now - this.pricingLoadTime) < this.PRICING_TTL) {
      return this.pricingCache
    }

    const response = await fetch(`${this.basePath}/case-${status}-prices.json`)
    if (!response.ok) {
      console.warn(`Failed to load Case ${status} pricing: ${response.statusText}`)
      return new Map()
    }

    const data: PricingData = await response.json()
    const priceMap = new Map<string, PriceInfo>()

    if (data?.prices) {
      Object.entries(data.prices).forEach(([id, price]) => {
        priceMap.set(id, price)
      })
    }

    this.pricingLoadTime = now
    return priceMap
  }

  /**
   * Merge list item with pricing data
   */
  private mergeWithPricing(item: CaseListItem, priceInfo?: PriceInfo): CaseListItem {
    return {
      ...item,
      regularPrice: priceInfo?.regularPrice ?? null,
      currentPrice: priceInfo?.currentPrice ?? null
    }
  }

  /**
   * Get all items (current + discontinued with pricing)
   */
  async getAllItems(): Promise<CaseListItem[]> {
    const [currentList, discontinuedList, currentPricing, discontinuedPricing] = await Promise.all([
      this.loadListFile('current'),
      this.loadListFile('discontinued'),
      this.loadPricingFile('current'),
      this.loadPricingFile('discontinued')
    ])

    const currentWithPricing = currentList.map(item =>
      this.mergeWithPricing(item, currentPricing.get(item.id))
    )
    const discontinuedWithPricing = discontinuedList.map(item =>
      this.mergeWithPricing(item, discontinuedPricing.get(item.id))
    )

    return [...currentWithPricing, ...discontinuedWithPricing]
  }

  /**
   * Get current items only (with pricing)
   */
  async getCurrentItems(): Promise<CaseListItem[]> {
    const [listItems, priceMap] = await Promise.all([
      this.loadListFile('current'),
      this.loadPricingFile('current')
    ])

    return listItems.map(item => this.mergeWithPricing(item, priceMap.get(item.id)))
  }

  /**
   * Get discontinued items only (with pricing)
   */
  async getDiscontinuedItems(): Promise<CaseListItem[]> {
    const [listItems, priceMap] = await Promise.all([
      this.loadListFile('discontinued'),
      this.loadPricingFile('discontinued')
    ])

    return listItems.map(item => this.mergeWithPricing(item, priceMap.get(item.id)))
  }

  /**
   * Get raw detail data for a specific Case
   */
  private async getRawDetail(itemId: string): Promise<CaseRawModel> {
    if (this.detailCache.has(itemId)) {
      return this.detailCache.get(itemId)!
    }

    const response = await fetch(`${this.basePath}/details/${itemId}.json`)
    if (!response.ok) {
      throw new Error(`Failed to load Case detail: ${response.statusText}`)
    }

    const rawData = await response.json()

    if (!isCaseRawModel(rawData)) {
      throw new Error(`Invalid Case data format for: ${itemId}`)
    }

    this.detailCache.set(itemId, rawData)
    return rawData
  }

  /**
   * Get detail data for a specific Case (List + Raw Detail + Pricing)
   */
  async getItemDetail(itemId: string): Promise<CaseDetailModel> {
    // 1. Find list item from either current or discontinued
    const [currentList, discontinuedList] = await Promise.all([
      this.loadListFile('current'),
      this.loadListFile('discontinued')
    ])

    const listItem = [...currentList, ...discontinuedList].find(item => item.id === itemId)
    if (!listItem) {
      throw new Error(`Case not found in list data: ${itemId}`)
    }

    // 2. Get pricing info
    const isDiscontinued = listItem.status === 'Discontinued'
    const priceMap = await this.loadPricingFile(isDiscontinued ? 'discontinued' : 'current')
    const priceInfo = priceMap.get(itemId)

    // 3. Get Raw detail
    const rawDetail = await this.getRawDetail(itemId)

    // 4. Merge: Raw Detail + List Pricing
    return {
      ...rawDetail,
      regularPrice: priceInfo?.regularPrice ?? null,
      currentPrice: priceInfo?.currentPrice ?? null,
      asin: listItem.asin
    } as CaseDetailModel
  }

  /**
   * Get multiple details at once
   */
  async getItemDetails(itemIds: string[]): Promise<CaseDetailModel[]> {
    return Promise.all(itemIds.map(id => this.getItemDetail(id)))
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.listCache.clear()
    this.pricingCache.clear()
    this.detailCache.clear()
    this.pricingLoadTime = 0
  }

  /**
   * Prefetch a detail for faster subsequent access
   */
  async prefetchDetail(itemId: string): Promise<void> {
    try {
      await this.getItemDetail(itemId)
    } catch {
      // Ignore prefetch errors
    }
  }
}

// Singleton instance
let adapterInstance: CaseDataAdapter | null = null

/**
 * Get the singleton instance of CaseDataAdapter
 */
export function getCaseDataAdapter(): CaseDataAdapter {
  adapterInstance ??= new CaseDataAdapter()
  return adapterInstance
}

/**
 * Reset the singleton instance (useful for testing)
 */
export function resetCaseDataAdapter(): void {
  adapterInstance?.clearCache()
  adapterInstance = null
}
